import { useState, useEffect, useMemo } from "react";
import { Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import SntDefaultUserIcon from "@icons/SntDefaultUserIcon";
import { apiClient } from "@/apis/apiClient";
import { loginSuccess } from "@/redux/actions";
import styled, { createGlobalStyle } from "styled-components";
import GlobalUtils from "@/utils/GlobalUtils";
import SntDialog from "@/components/SntDialog/SntDialog";
import { translate } from "@/hooks/useLanguage";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { LoginInfo, AccountSummary } from "@/types/LoginInfo";
import authenticationClient from "@/apis/authenticationClient";

// Unified account item for displaying in the list
interface UnifiedAccountItem {
  id: string;
  name: string;
  organisationName: string;
  roleName?: string;
  image?: string;
  isCurrent: boolean;
  // Original data for switch operations
  originalAccount?: AccountSummary;
}

interface AccountSwitchDialogProps {
  show: boolean;
  onHide: () => void;
  currentUser?: LoginInfo;
  openedFrom?: "header" | "complete";
}

interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
}

const AccountItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  margin-bottom: 8px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;

  &:hover:not(.current) {
    background: #f8f9fa;
    border-color: #dee2e6;
  }

  &.current {
    background: #e3f2fd;
    border-color: #2196f3;
  }

  &.current.disabled {
    cursor: default;
  }

  &.current:not(.disabled) {
    cursor: pointer;
  }

  .avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    margin-right: 12px;
    flex-shrink: 0;
  }

  .account-info {
    flex: 1;
    min-width: 0;

    .user-name {
      font-weight: 500;
      color: #333;
      margin-bottom: 2px;
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }

`;

const CurrentBadge = styled.span`
  font-size: 0.75rem;
  color: #2196f3;
  font-weight: 500;
`;

const OrgRoleBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8125rem;
  margin-top: 2px;

  .org-name {
    color: #555;
    font-weight: 500;
  }

  .role-badge {
    color: #555;
    font-size: 0.75rem;
    padding: 1px 8px;
    background: #e9ecef;
    border-radius: 10px;
  }
`;

const AccountSwitchDialogStyles = createGlobalStyle`
  .account-switch-dialog-centered .modal-dialog {
    display: flex;
    align-items: center;
    min-height: calc(100vh - 1rem);
    margin: 0.5rem auto;
    max-width: 600px;
  }

  .account-switch-dialog-centered .modal-content {
    margin: auto;
  }
`;

const AccountSwitchDialog = ({
  show,
  onHide,
  currentUser,
  openedFrom = "header",
}: AccountSwitchDialogProps) => {
  const [errorDialogMessage, setErrorDialogMessage] = useState("");
  const [isShowErrorDialog, setShowErrorDialog] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [switchingItemId, setSwitchingItemId] = useState<string | null>(null);
  const dispatch = useDispatch();

  // Build list of available accounts for display
  const unifiedItems = useMemo<UnifiedAccountItem[]>(() => {
    const currentUserId = currentUser?.userId;
    const accounts = currentUser?.availableAccounts || [];

    return accounts
      .filter((account) => account.id)
      .map((account) => ({
        id: `account-${account.id}`,
        name: account.fullName || account.username || "",
        organisationName: account.partnerName || account.organisationName || "",
        roleName: account.roleName,
        image: account.image,
        isCurrent: account.id === currentUserId,
        originalAccount: account,
      }))
      .sort((a, b) => {
        // Current user first, then by name, org, role
        if (a.isCurrent && !b.isCurrent) return -1;
        if (!a.isCurrent && b.isCurrent) return 1;
        const nameCompare = a.name.localeCompare(b.name);
        if (nameCompare !== 0) return nameCompare;
        const orgCompare = a.organisationName.localeCompare(b.organisationName);
        if (orgCompare !== 0) return orgCompare;
        return (a.roleName || "").localeCompare(b.roleName || "");
      });
  }, [currentUser?.availableAccounts, currentUser?.userId]);

  // Reset error when dialog opens
  useEffect(() => {
    if (show) {
      setShowErrorDialog(false);
      setErrorDialogMessage("");
    }
  }, [show]);

  const switchAccount = async (accountId: string): Promise<void> => {
    setSwitching(true);
    setSwitchingItemId(`account-${accountId}`);
    try {
      const response = await apiClient.put(
        `/rest/authentication/accounts/switch/${accountId}`
      );

      // Update authentication state with new login info
      dispatch(loginSuccess(response.data));

      // Close dialog and redirect to home page to reflect new user context
      onHide();
      window.location.href = "/";
    } catch (err) {
      const apiError = err as ApiError;
      setErrorDialogMessage(
        apiError.response?.data?.message ||
          translate(LanguageKey.error_switching_account_key)
      );
      setShowErrorDialog(true);
    } finally {
      setSwitching(false);
      setSwitchingItemId(null);
    }
  };

  const switchOrganization = async (orgId: number, userId: string): Promise<void> => {
    setSwitching(true);
    setSwitchingItemId(`account-${userId}`);
    try {
      const response = await authenticationClient.switchOrganization(orgId, userId);

      // Update authentication state with new login info
      dispatch(loginSuccess(response.data));

      // Close dialog and redirect to home page to reflect new user context
      onHide();
      window.location.href = "/";
    } catch (err) {
      const apiError = err as ApiError;
      setErrorDialogMessage(
        apiError.response?.data?.message ||
          translate(LanguageKey.have_not_the_permission_key)
      );
      setShowErrorDialog(true);
    } finally {
      setSwitching(false);
      setSwitchingItemId(null);
    }
  };

  const handleItemClick = (item: UnifiedAccountItem) => {
    if (item.isCurrent) {
      // Current item clicked - just close dialog
      onHide();
      return;
    }

    if (switching) return;

    const account = item.originalAccount;
    if (!account?.id) return;

    // Same org as current user -> switch user, different org -> switch organization
    if (account.orgId === currentUser?.organisation?.id) {
      switchAccount(account.id);
    } else if (account.orgId) {
      switchOrganization(account.orgId, account.id);
    }
  };

  const renderItem = (item: UnifiedAccountItem) => {
    const isItemSwitching = switchingItemId === item.id;

    return (
      <AccountItem
        key={item.id}
        className={`${item.isCurrent ? "current" : ""} ${
          item.isCurrent && openedFrom === "header" ? "disabled" : ""
        }`}
        onClick={() => handleItemClick(item)}
      >
        {item.image ? (
          <img
            src={GlobalUtils.getFile(
              "user",
              item.originalAccount?.id || 0,
              "avatar",
              item.image
            )}
            alt={item.name}
            className="avatar"
          />
        ) : (
          <SntDefaultUserIcon size={40} className="avatar" />
        )}

        <div className="account-info">
          <div className="user-name">
            {item.name}
            {item.isCurrent && <CurrentBadge>({translate(LanguageKey.v43_current_key)})</CurrentBadge>}
          </div>
          {(item.organisationName || item.roleName) && (
            <OrgRoleBadge>
              {item.organisationName && <span className="org-name">{item.organisationName}</span>}
              {item.roleName && <span className="role-badge">{item.roleName}</span>}
            </OrgRoleBadge>
          )}
        </div>

        {isItemSwitching && <Spinner animation="border" size="sm" />}
      </AccountItem>
    );
  };

  const hasItems = unifiedItems.length > 0;

  return (
    <>
      <AccountSwitchDialogStyles />
      <SntDialog
        isShow={isShowErrorDialog}
        onHide={() => setShowErrorDialog(false)}
        onClose={() => setShowErrorDialog(false)}
        isShowSaveButton={false}
        isShowCloseButton={true}
        title={translate(LanguageKey.error_key)}
      >
        {() => <div>{errorDialogMessage}</div>}
      </SntDialog>
      <SntDialog
        isShow={show}
        onHide={onHide}
        onClose={onHide}
        title={translate(LanguageKey.select_account_key)}
        size="sm"
        isShowSaveButton={false}
        isShowCloseButton={openedFrom === "header"}
        customModalClassname="account-switch-dialog-centered"
      >
        {() => (
          <>
            {!hasItems ? (
              <div className="text-muted">{translate(LanguageKey.no_additional_accounts_key)}</div>
            ) : (
              <div>
                {unifiedItems.map((item) => renderItem(item))}
              </div>
            )}
          </>
        )}
      </SntDialog>
    </>
  );
};

export default AccountSwitchDialog;
